/**
 * Bibliotheque de jeux persistante.
 *
 * Deux origines coexistent :
 *  - `server` : les jeux declares dans /library.json, servis par le serveur.
 *  - `local`  : les ISO deposees par le visiteur. On conserve un handle de
 *               fichier (File System Access) quand le navigateur le permet,
 *               ce qui evite de recopier 1,4 Go dans IndexedDB. Sinon on
 *               retombe sur le stockage du Blob lui-meme.
 *
 * Rien n'est televerse : les jeux locaux ne quittent jamais la machine.
 */

const DB_NAME = 'ps2-library';
const DB_VERSION = 1;
const STORE = 'entries';

export type EntryOrigin = 'server' | 'local';

export interface LibraryEntry {
	id: string;
	title: string;
	/** Code disque (SLES-504.80). Vide tant qu'on ne l'a pas lu dans l'ISO. */
	code: string;
	region: string;
	size: number;
	origin: EntryOrigin;
	/** Pour les jeux serveur : URL de l'image disque. */
	url?: string;
	/** Horodatage de la derniere session, en millisecondes. */
	lastPlayed: number | null;
	/** Poignee de fichier, si le navigateur gere File System Access. */
	handle?: any;
	/** Repli : le fichier lui-meme, quand il n'y a pas de poignee. */
	blob?: Blob;
	fileName?: string;
}

function openDb(): Promise<IDBDatabase> {
	return new Promise((resolve, reject) => {
		const request = indexedDB.open(DB_NAME, DB_VERSION);
		request.onupgradeneeded = () => {
			const db = request.result;
			if (!db.objectStoreNames.contains(STORE)) {
				db.createObjectStore(STORE, { keyPath: 'id' });
			}
		};
		request.onsuccess = () => resolve(request.result);
		request.onerror = () => reject(request.error);
	});
}

function tx<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
	return openDb().then(
		(db) =>
			new Promise<T>((resolve, reject) => {
				const transaction = db.transaction(STORE, mode);
				const request = run(transaction.objectStore(STORE));
				request.onsuccess = () => resolve(request.result);
				request.onerror = () => reject(request.error);
			}),
	);
}

export async function loadLocalEntries(): Promise<LibraryEntry[]> {
	try {
		const all = await tx<LibraryEntry[]>('readonly', (store) => store.getAll() as any);
		return all ?? [];
	} catch {
		// Navigation privee, quota refuse : on degrade en bibliotheque volatile.
		return [];
	}
}

export async function putEntry(entry: LibraryEntry): Promise<void> {
	try {
		await tx('readwrite', (store) => store.put(entry) as any);
	} catch {
		/* sans persistance, l'entree reste seulement en memoire */
	}
}

export async function deleteEntry(id: string): Promise<void> {
	try {
		await tx('readwrite', (store) => store.delete(id) as any);
	} catch {
		/* ignore */
	}
}

/**
 * Les jeux proposes par le serveur. Absence de fichier ou JSON invalide =
 * bibliotheque purement locale, ce qui est un mode de fonctionnement normal.
 */
export async function loadServerEntries(): Promise<LibraryEntry[]> {
	try {
		const response = await fetch('library.json', { cache: 'no-cache' });
		if (!response.ok) return [];
		const raw = await response.json();
		if (!Array.isArray(raw)) return [];
		return raw.map((item: any, index: number) => ({
			id: `server:${item.id ?? item.url ?? index}`,
			title: String(item.title ?? 'Sans titre'),
			code: String(item.code ?? ''),
			region: String(item.region ?? ''),
			size: Number(item.size ?? 0),
			origin: 'server' as EntryOrigin,
			url: String(item.url ?? ''),
			lastPlayed: null,
		}));
	} catch {
		return [];
	}
}

/** Formate une taille en unites binaires, avec la virgule francaise. */
export function formatSize(bytes: number): string {
	if (!bytes) return '—';
	const giga = bytes / 1073741824;
	if (giga >= 1) return `${giga.toFixed(2).replace('.', ',')} Go`;
	return `${Math.round(bytes / 1048576)} Mo`;
}

export function formatLastPlayed(timestamp: number | null): string {
	if (!timestamp) return 'jamais lancé';
	const elapsed = Date.now() - timestamp;
	const minutes = Math.floor(elapsed / 60000);
	if (minutes < 1) return "à l'instant";
	if (minutes < 60) return `il y a ${minutes} min`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `il y a ${hours} h`;
	const days = Math.floor(hours / 24);
	if (days === 1) return 'hier';
	if (days < 30) return `il y a ${days} j`;
	return new Date(timestamp).toLocaleDateString('fr-FR');
}
